import { useState, useEffect } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import { Plus, Edit2, Trash2, Power, Eye, Upload, FileText, X, Search, Save, Loader2, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabaseClient';
import { safeFetch } from '@/lib/safeFetch';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

interface BlogPost {
    id: string;
    title: string;
    slug: string;
    short_description: string;
    content: string;
    thumbnail: string;
    status: 'draft' | 'published';
    author_name: string;
    tags: string;
    created_at: string;
}

const AdminBlogs = () => {
    const [blogs, setBlogs] = useState<BlogPost[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [currentBlog, setCurrentBlog] = useState<Partial<BlogPost>>({
        status: 'draft',
        author_name: 'Ratchagan Analysis'
    });
    const [isUploading, setIsUploading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        loadBlogs();
    }, []);

    const loadBlogs = async () => {
        setIsLoading(true);
        const data = await safeFetch<BlogPost[]>(
            supabase.from('blog_posts').select('*').order('created_at', { ascending: false }),
            [],
            'Loading blogs'
        );
        setBlogs(data);
        setIsLoading(false);
    };

    const generateSlug = (title: string) => {
        return title
            .toLowerCase()
            .replace(/[^\w\s-]/g, '')
            .replace(/[\s_-]+/g, '-')
            .replace(/^-+|-+$/g, '');
    };

    const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const title = e.target.value;
        setCurrentBlog(prev => ({
            ...prev,
            title,
            slug: prev.id ? prev.slug : generateSlug(title) // Only auto-gen slug for new posts
        }));
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setIsUploading(true);
        try {
            const fileExt = file.name.split('.').pop();
            const fileName = `${Math.random()}.${fileExt}`;
            const blogId = currentBlog.id || 'new';
            const filePath = `${blogId}/${fileName}`;

            const { error: uploadError } = await supabase.storage
                .from('blog-thumbnails')
                .upload(filePath, file);

            if (uploadError) throw uploadError;

            const { data: { publicUrl } } = supabase.storage
                .from('blog-thumbnails')
                .getPublicUrl(filePath);

            setCurrentBlog(prev => ({ ...prev, thumbnail: publicUrl }));
            toast.success('Thumbnail uploaded successfully');
        } catch (error: any) {
            toast.error('Upload failed: ' + error.message);
        } finally {
            setIsUploading(false);
        }
    };

    const saveBlog = async () => {
        if (!currentBlog.title || !currentBlog.slug || !currentBlog.content) {
            toast.error('Please fill in required fields (Title, Slug, Content)');
            return;
        }

        setIsSaving(true);
        try {
            const blogData = {
                ...currentBlog,
                updated_at: new Date().toISOString()
            };

            let res;
            if (currentBlog.id) {
                res = await supabase.from('blog_posts').update(blogData).eq('id', currentBlog.id);
            } else {
                res = await supabase.from('blog_posts').insert([blogData]);
            }

            if (res.error) throw res.error;

            toast.success('Blog saved successfully');
            setIsEditing(false);
            loadBlogs();
        } catch (error: any) {
            toast.error('Save failed: ' + error.message);
        } finally {
            setIsSaving(false);
        }
    };

    const toggleStatus = async (blog: BlogPost) => {
        const newStatus = blog.status === 'published' ? 'draft' : 'published';
        try {
            const { error } = await supabase
                .from('blog_posts')
                .update({ status: newStatus })
                .eq('id', blog.id);

            if (error) throw error;
            loadBlogs();
            toast.success(`Blog ${newStatus === 'published' ? 'published' : 'moved to drafts'}`);
        } catch (error: any) {
            toast.error('Update failed: ' + error.message);
        }
    };

    const deleteBlog = async (id: string) => {
        if (!confirm('Are you sure you want to delete this blog?')) return;

        try {
            const { error } = await supabase.from('blog_posts').delete().eq('id', id);
            if (error) throw error;
            toast.success('Blog deleted');
            loadBlogs();
        } catch (error: any) {
            toast.error('Delete failed: ' + error.message);
        }
    };

    const filteredBlogs = blogs.filter(blog =>
        blog.title.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <AdminLayout>
            <div className="space-y-10">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div>
                        <h2 className="text-3xl font-black text-slate-900 uppercase tracking-tighter">Blog Repository</h2>
                        <p className="text-slate-500 font-medium text-sm">Manage your insights and research publications.</p>
                    </div>
                    <Button
                        onClick={() => {
                            setCurrentBlog({ status: 'draft', author_name: 'Ratchagan Analysis' });
                            setIsEditing(true);
                        }}
                        className="btn-navy h-14 rounded-2xl px-8 text-[10px] font-black uppercase tracking-widest shadow-premium"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        New Publication
                    </Button>
                </div>

                {/* Search Bar */}
                <div className="relative max-w-md">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                        placeholder="Search publications..."
                        className="pl-12 h-12 rounded-xl bg-white border-slate-100 shadow-sm"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>

                <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-slate-50 border-b border-slate-100">
                                <tr>
                                    <th className="px-8 py-4 text-left text-[9px] font-black uppercase tracking-widest text-slate-400">Publication</th>
                                    <th className="px-8 py-4 text-left text-[9px] font-black uppercase tracking-widest text-slate-400">Status</th>
                                    <th className="px-8 py-4 text-left text-[9px] font-black uppercase tracking-widest text-slate-400">Date</th>
                                    <th className="px-8 py-4 text-right text-[9px] font-black uppercase tracking-widest text-slate-400">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {isLoading ? (
                                    <tr><td colSpan={4} className="px-8 py-10 text-center text-[10px] font-black uppercase tracking-widest text-slate-400 cursor-wait">Accessing Archives...</td></tr>
                                ) : filteredBlogs.length === 0 ? (
                                    <tr><td colSpan={4} className="px-8 py-10 text-center text-[10px] font-black uppercase tracking-widest text-slate-400">No records found</td></tr>
                                ) : filteredBlogs.map((blog) => (
                                    <tr key={blog.id} className="hover:bg-slate-50/50 transition-colors group">
                                        <td className="px-8 py-6">
                                            <div className="flex items-center gap-4">
                                                <div className="w-16 h-12 rounded-xl bg-slate-100 overflow-hidden shrink-0 border border-slate-200">
                                                    {blog.thumbnail ? (
                                                        <img src={blog.thumbnail} className="w-full h-full object-cover" alt="" />
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center text-slate-300">
                                                            <FileText className="w-6 h-6" />
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex flex-col min-w-0">
                                                    <span className="text-[11px] font-black text-slate-900 uppercase tracking-tight truncate max-w-[300px]">{blog.title}</span>
                                                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest truncate max-w-[200px]">{blog.slug}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-8 py-6">
                                            <span className={`px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest border ${blog.status === 'published'
                                                ? 'bg-emerald-50 text-emerald-600 border-emerald-100'
                                                : 'bg-amber-50 text-amber-600 border-amber-100'
                                                }`}>
                                                {blog.status === 'published' ? 'Published' : 'Draft'}
                                            </span>
                                        </td>
                                        <td className="px-8 py-6 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                            {new Date(blog.created_at).toLocaleDateString()}
                                        </td>
                                        <td className="px-8 py-6">
                                            <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <button
                                                    onClick={() => {
                                                        setCurrentBlog(blog);
                                                        setIsEditing(true);
                                                    }}
                                                    className="p-3 bg-slate-50 hover:bg-slate-100 text-slate-400 hover:text-primary rounded-xl transition-all border border-slate-200 shadow-sm"
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                </button>
                                                <button onClick={() => toggleStatus(blog)} className="p-3 bg-slate-50 hover:bg-slate-100 text-slate-400 hover:text-primary rounded-xl transition-all border border-slate-200 shadow-sm">
                                                    <Power className="w-4 h-4" />
                                                </button>
                                                <button onClick={() => deleteBlog(blog.id)} className="p-3 bg-slate-50 hover:bg-destructive/5 text-slate-400 hover:text-destructive rounded-xl transition-all border border-slate-200 shadow-sm">
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Blog Editor Modal */}
            <Dialog open={isEditing} onOpenChange={setIsEditing}>
                <DialogContent className="rounded-[2.5rem] p-0 max-w-4xl max-h-[90vh] overflow-hidden flex flex-col border-none shadow-2xl">
                    <div className="p-8 border-b border-slate-100 bg-white sticky top-0 z-20 flex justify-between items-center">
                        <div>
                            <DialogTitle className="text-xl font-black text-slate-900 uppercase tracking-tight">
                                {currentBlog.id ? 'Edit Publication' : 'New Research Entry'}
                            </DialogTitle>
                            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-1">
                                {currentBlog.id ? `ID: ${currentBlog.id}` : 'Drafting System Active'}
                            </p>
                        </div>
                        <Button
                            onClick={saveBlog}
                            disabled={isSaving}
                            className="btn-gold h-12 rounded-xl px-8 text-[10px] font-black uppercase tracking-widest shadow-premium"
                        >
                            {isSaving ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : (
                                <Save className="w-4 h-4 mr-2" />
                            )}
                            Finalize Report
                        </Button>
                    </div>

                    <div className="p-10 overflow-y-auto bg-slate-50/30 flex-1">
                        <div className="grid lg:grid-cols-3 gap-10">
                            {/* Left Side: Details */}
                            <div className="lg:col-span-2 space-y-8">
                                <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm space-y-6">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Report Title</label>
                                        <Input
                                            value={currentBlog.title || ''}
                                            onChange={handleTitleChange}
                                            placeholder="Enter report title..."
                                            className="h-14 rounded-xl border-slate-100 text-sm font-bold placeholder:text-slate-300 focus:ring-accent"
                                        />
                                    </div>

                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Universal Slug (URL)</label>
                                            <Input
                                                value={currentBlog.slug || ''}
                                                onChange={(e) => setCurrentBlog(prev => ({ ...prev, slug: generateSlug(e.target.value) }))}
                                                placeholder="url-friendly-slug"
                                                className="h-12 rounded-xl border-slate-100 text-xs font-medium"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Author / Source</label>
                                            <Input
                                                value={currentBlog.author_name || 'Ratchagan Analysis'}
                                                onChange={(e) => setCurrentBlog(prev => ({ ...prev, author_name: e.target.value }))}
                                                className="h-12 rounded-xl border-slate-100 text-xs font-medium"
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Short Brief (Excerpt)</label>
                                        <Textarea
                                            value={currentBlog.short_description || ''}
                                            onChange={(e) => setCurrentBlog(prev => ({ ...prev, short_description: e.target.value }))}
                                            placeholder="Quick summary of the publication..."
                                            className="min-h-[100px] rounded-xl border-slate-100 text-xs font-medium resize-none"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Research Content (Full Report)</label>
                                        <Textarea
                                            value={currentBlog.content || ''}
                                            onChange={(e) => setCurrentBlog(prev => ({ ...prev, content: e.target.value }))}
                                            placeholder="Write your research content here..."
                                            className="min-h-[300px] rounded-xl border-slate-100 text-xs font-medium resize-none leading-relaxed"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Right Side: Meta */}
                            <div className="space-y-8">
                                <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm space-y-6">
                                    <div className="space-y-4">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Visual Cover (Thumbnail)</label>
                                        <div className="aspect-[16/10] rounded-2xl bg-slate-50 border-2 border-dashed border-slate-100 overflow-hidden relative group">
                                            {currentBlog.thumbnail ? (
                                                <>
                                                    <img src={currentBlog.thumbnail} className="w-full h-full object-cover" alt="" />
                                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                        <label className="cursor-pointer p-4 bg-white/20 backdrop-blur-md rounded-full text-white hover:bg-white/40 transition-all">
                                                            <Upload className="w-5 h-5" />
                                                            <input type="file" className="hidden" accept="image/*" onChange={handleFileUpload} />
                                                        </label>
                                                    </div>
                                                </>
                                            ) : (
                                                <label className="flex flex-col items-center justify-center w-full h-full cursor-pointer hover:bg-slate-100/50 transition-colors">
                                                    {isUploading ? (
                                                        <Loader2 className="w-6 h-6 text-slate-300 animate-spin" />
                                                    ) : (
                                                        <>
                                                            <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center mb-3">
                                                                <Upload className="w-5 h-5 text-slate-400" />
                                                            </div>
                                                            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Upload Image</span>
                                                        </>
                                                    )}
                                                    <input type="file" className="hidden" accept="image/*" onChange={handleFileUpload} disabled={isUploading} />
                                                </label>
                                            )}
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Tags (Comma Separated)</label>
                                        <Input
                                            value={currentBlog.tags || ''}
                                            onChange={(e) => setCurrentBlog(prev => ({ ...prev, tags: e.target.value }))}
                                            placeholder="Stock Market, Nifty, Strategy"
                                            className="h-12 rounded-xl border-slate-100 text-xs font-medium"
                                        />
                                    </div>

                                    <div className="space-y-2 pt-4">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Status Protocol</label>
                                        <div className="flex bg-slate-50 p-1.5 rounded-2xl border border-slate-100">
                                            <button
                                                onClick={() => setCurrentBlog(prev => ({ ...prev, status: 'draft' }))}
                                                className={cn(
                                                    "flex-1 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all",
                                                    currentBlog.status === 'draft' ? "bg-white text-primary shadow-sm" : "text-slate-400 hover:text-slate-600"
                                                )}
                                            >
                                                Draft
                                            </button>
                                            <button
                                                onClick={() => setCurrentBlog(prev => ({ ...prev, status: 'published' }))}
                                                className={cn(
                                                    "flex-1 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all",
                                                    currentBlog.status === 'published' ? "bg-emerald-500 text-white shadow-lg shadow-emerald-200" : "text-slate-400 hover:text-slate-600"
                                                )}
                                            >
                                                Publish
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-primary/5 p-8 rounded-3xl border border-primary/10">
                                    <h4 className="text-[10px] font-black uppercase tracking-widest text-primary mb-4">Research Meta</h4>
                                    <div className="space-y-3">
                                        <div className="flex justify-between items-center text-[9px] font-bold text-slate-500 uppercase tracking-widest">
                                            <span>Read Time Est.</span>
                                            <span className="text-primary">{Math.max(1, Math.ceil((currentBlog.content?.split(' ').length || 0) / 200))} MIN</span>
                                        </div>
                                        <div className="flex justify-between items-center text-[9px] font-bold text-slate-500 uppercase tracking-widest">
                                            <span>SEO Visibility</span>
                                            <span className={cn(currentBlog.slug?.length > 10 ? "text-emerald-500" : "text-amber-500")}>OPTIMIZED</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </AdminLayout>
    );
};

export default AdminBlogs;
